<template>
  <div class="product-tile">
    <u-i-image-handler
      v-if="productImage"
      class="product-tile__cover"
      :img-loading="enableLazyLoad ? 'lazy' : 'eager'"
      img-format="avif"
      img-sizes="250px xxl:350px"
      :img-src="productImage" />
    <div class="product-tile__names">
      <div class="product-tile__product-name" v-html="productName" />
      <nuxt-link
        v-if="vendorSlug"
        class="product-tile__vendor-name"
        :to="`/shop/sound-designer/${vendorSlug}/`">{{ vendorName }}</nuxt-link>
      <div
        v-else
        class="product-tile__vendor-name">{{ vendorName }}</div>
    </div>
    <product-price
      :key="productId"
      class="product-tile__price"
      :is-from-price="!!productVariations"
      :default-currency="defaultCurrency"
      :product-id="productId" />
    <buy-product
      class="product-tile__buy"
      :product-id="productId"
      :product-name="productName"
      :product-variations="productVariations"
      :product-subscription-options="productSubscriptionOptions"
      :product-details="productDetails"
      :default-currency="defaultCurrency"
      variation-selector-available
      button-style="button-only button-large" />
    <product-sale-badge
      v-if="displaySaleBadge?.percentageSaleBadge"
      :percentage-discount="displaySaleBadge.percentageSaleBadge"
      :show-ends-soon="displaySaleBadge.showEndsSoonBadge"
      :show-ends-today="displaySaleBadge.showEndsTodayBadge" />
    <div class="product-tile__overlay">
      <product-like
        class="product-tile__overlay-like"
        :product-name="productName"
        :product-id="productId" />
      <product-sale-badge
        v-if="displaySaleBadge?.percentageSaleBadge"
        :percentage-discount="displaySaleBadge.percentageSaleBadge"
        :show-ends-soon="displaySaleBadge.showEndsSoonBadge"
        :show-ends-today="displaySaleBadge.showEndsTodayBadge" />
      <div class="product-tile__overlay-play">
        <product-play
          v-if="productDetails?.audioDemos?.length || productDetails?.soundcloudId"
          medium-size
          :product-details="productDetails" />
      </div>
      <div v-if="hasAdditionalMetadata" class="product-tile__overlay-meta">
        <product-meta
          :product-name="productName"
          :total-duration="productSpecs?.totalDuration"
          :sounds-included="productSpecs?.soundsIncluded"
          :files-included="productSpecs?.filesIncluded" />
      </div>
      <div class="product-tile__overlay-link">
        <nuxt-link class="product-tile__overlay-link-tag" :to="productLink" @click="storeBasicProductDetails">
          Learn More
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BuyProduct from "~/components/BuyProduct.vue";
import ProductLike from "~/components/ProductLike.vue";
import ProductPlay from "~/components/ProductPlay.vue";
import type { ApiProductSpecs, ProductSubscriptionOptions } from "~/stores/types/searchTermInterface";
import { computed, defineAsyncComponent, useProductStore, toRefs, ref, watch } from "#imports";
import { usePriceStore } from "~/stores/priceStore";
import { useUserStore } from "~/stores/user";

const productMeta = defineAsyncComponent(() => import('~/components/parts/product/ProductMeta.vue'));
const priceStore = usePriceStore();
const userStore = useUserStore();

const props = defineProps<{
  productId: string|number,
  productImage: string|boolean,
  productName: string,
  productLink: string,
  productLiked?: boolean,
  vendorName?: string,
  vendorSlug?: string,
  defaultCurrency: {
    type: object,
    required: true,
  },
  saleBadges?: object
  soundcloudId?: string,
  soundcloudIdType?: string,
  productVariations?: object,
  productSubscriptionOptions?: ProductSubscriptionOptions|null,
  productSpecs: ApiProductSpecs|null,
  enableLazyLoad?: boolean,
}>();

const { vendorName, vendorSlug, productSpecs } = toRefs(props)

// Track if we're using client-side calculated badges (after currency change)
const useClientSideBadges = ref(false);
const clientSideBadge = ref<any>(null);
const currentCurrency = computed(() => userStore.getSelectedCurrency);

// Fetch prices and recalculate badges when currency changes
async function recalculateBadgesForCurrency() {
  if (process.server) return;

  try {
    useClientSideBadges.value = true;

    // For variable products, find the HIGHEST percentage discount and show "UP TO X%"
    if (props.productVariations?.length) {
      let highestPercentage = 0;
      let highestVariationBadge = null;

      for (const variation of props.productVariations) {
        // Refresh only on first variation to clear stale prices, then reuse cache
        const isFirstVariation = props.productVariations[0].databaseId === variation.databaseId;
        const priceData = await priceStore.getPrice(variation.databaseId, isFirstVariation);

        if (priceData && priceData.percentageDiscount) {
          const percentage = parseInt(priceData.percentageDiscount, 10);

          if (percentage > highestPercentage) {
            highestPercentage = percentage;
            highestVariationBadge = {
              percentageSaleBadge: `UP TO ${percentage}%`,
              showEndsSoonBadge: variation.productSaleBadges?.showEndsSoonBadge || false,
              showEndsTodayBadge: variation.productSaleBadges?.showEndsTodayBadge || false,
            };
          }
        }
      }

      clientSideBadge.value = highestVariationBadge;
    } else {
      // For simple products, use backend percentage directly
      const priceData = await priceStore.getPrice(props.productId, true);

      if (priceData?.percentageDiscount) {
        clientSideBadge.value = {
          percentageSaleBadge: `${priceData.percentageDiscount}%`,
          showEndsSoonBadge: props.saleBadges?.showEndsSoonBadge || false,
          showEndsTodayBadge: props.saleBadges?.showEndsTodayBadge || false,
        };
      } else {
        clientSideBadge.value = null;
      }
    }
  } catch (error) {
    console.error('Error recalculating sale badges:', error);
    useClientSideBadges.value = false;
  }
}

// Watch for currency changes
watch(currentCurrency, () => {
  recalculateBadgesForCurrency();
});

// Computed property to get the best sale badge (server-side or client-side)
const displaySaleBadge = computed(() => {
  // Use client-side calculated badge if currency has changed
  if (useClientSideBadges.value) {
    return clientSideBadge.value;
  }

  // Otherwise use server-side badges (SSR + default currency)
  // For variable products, find the HIGHEST percentage discount and show "UP TO X%"
  if (props.productVariations?.length) {
    let highestPercentage = 0;
    let highestVariationBadge = null;

    for (const variation of props.productVariations) {
      if (variation.productSaleBadges?.percentageSaleBadge) {
        const percentage = parseInt(variation.productSaleBadges.percentageSaleBadge, 10);

        if (percentage > highestPercentage) {
          highestPercentage = percentage;
          highestVariationBadge = {
            percentageSaleBadge: `UP TO ${percentage}%`,
            showEndsSoonBadge: variation.productSaleBadges.showEndsSoonBadge,
            showEndsTodayBadge: variation.productSaleBadges.showEndsTodayBadge,
          };
        }
      }
    }

    if (highestVariationBadge) {
      return highestVariationBadge;
    }
  }

  // Fall back to parent product sale badge
  return props.saleBadges;
});

const productDetails = computed(() => {
  return {
    productId: props.productId,
    productImage: props.productImage,
    productName: props.productName,
    productLiked: props.productLiked,
    productLink: props.productLink,
    defaultCurrency: props.defaultCurrency,
    soundcloudId: props.soundcloudId,
    soundcloudIdType: props.soundcloudIdType,
    audioDemos: props.productSpecs?.audioDemos ?? [],
    variations: props.productVariations,
    productVendor: {
      name: props.vendorName ?? '',
      slug: props.vendorSlug ?? ''
    },
  }
});

const hasAdditionalMetadata = computed<boolean>(() => {
  return !!props.productSpecs && (props.productSpecs.soundsIncluded || props.productSpecs.filesIncluded || props.productSpecs.totalDuration);
});

const storeBasicProductDetails = () => {
  const productStore = useProductStore()

  const basicProductDetails = {
    productId: props.productId,
    databaseId: props.productId,
    image: {
      mediaItemUrl: props.productImage,
      altText: props.productName,
    },
    name: props.productName,
    slug: props.productLink,
    defaultCurrency: props.defaultCurrency,
    soundcloudId: props.soundcloudId,
    soundcloudIdType: props.soundcloudIdType,
    audioDemos: props.productSpecs?.audioDemos ?? [],
    productVendor: {
      name: props.vendorName,
      slug: props.vendorSlug,
    },
    variations: {
      nodes: props.productVariations
    },
    productSubscriptionOptions: props.productSubscriptionOptions,
  }

  productStore.storeBasicProductDetails(basicProductDetails)
}
</script>

<style lang="scss">
@use "~/common/scss/button";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.product-tile {
  $root: &;

  display: grid;
  font-size: helpers.px-to-rem(12);
  gap: .4rem .4rem;
  grid-template: "cover cover" auto "name price" auto "buy buy" auto / 1fr auto;
  max-width: 50vh;
  position: relative;

  &__cover {
    aspect-ratio: 1 / 1;
    background-color: #FFFFFF;
    border-radius: 4px 4px;
    border: 1px solid #E0E0E0;
    box-sizing: border-box;
    grid-area: cover;
    height: 100%;
    object-fit: contain;
    overflow: hidden;
    width: 100%;
  }

  &__names {
    display: flex;
    flex-flow: column;
    grid-area: name;
    justify-content: center;
    overflow: hidden;
    text-align: left;
  }

  &__product-name,
  &__vendor-name {
    color: currentColor;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__product-name {
    font-weight: bold;
  }

  &__vendor-name {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__names,
  &__price {
    line-height: 1.3em;
  }

  &__price {
    --price-font-size: 1em;

    display: flex;
    flex-flow: column;
    grid-area: price;
    text-align: right;
  }

  &__buy {
    grid-area: buy;
  }

  /* DESKTOP */
  @include mq.aboveTablet() {
    &__overlay {
      --child-opacity: 0;

      align-items: center;
      aspect-ratio: 1 / 1;
      background-color: transparent;
      backdrop-filter: blur(0);
      border-radius: 4px 4px;
      color: #FFF;
      display: grid;
      grid-area: cover;
      grid-template: "play" 1fr "separator" auto "meta" auto "link" 1fr / 100%;
      height: 100%;
      justify-content: center;
      position: relative;
      transition: backdrop-filter .25s, background-color .25s;
      width: 100%;

      &:focus,
      &:active,
      &:hover {
        --child-opacity: 1;
        display: grid;
        background-color: #33333333;
        backdrop-filter: blur(.4rem);
      }
    }

    &__overlay-play,
    &__overlay-separator,
    &__overlay-link,
    &__overlay-meta {
      align-items: center;
      justify-content: center;
      opacity: var(--child-opacity);
      transition: opacity .25s;
    }

    &__overlay-link {
      display: flex;
    }

    &__overlay-like {
      color: #FFF;
      font-size: helpers.px-to-rem(22);
      left: helpers.px-to-rem(10);
      opacity: var(--child-opacity);
      position: absolute;
      top: helpers.px-to-rem(8);
      transition: opacity .25s;

      &.product-like--selected {
        opacity: 1;
      }
    }
  }

  &__overlay-play {
    align-items: center;
    color: #FFF;
    display: flex;
    font-size: helpers.px-to-rem(46);
    grid-area: play;
    justify-content: center;
    justify-self: center;
    z-index: 4;
  }

  &__overlay-separator {
    background-color: #FFF;
    grid-area: separator;
    height: 1px;
    margin: auto auto -.05rem;
    width: 66%;
  }

  &__overlay-meta-span {
    text-align: center;
    max-width: 100%;
  }

  &__overlay-meta {
    display: flex;
    flex-flow: row wrap;
    flex-direction: column;
    align-items: center;
    gap: .25rem;
    grid-area: meta;
    font-weight: bold;
    text-align: center;
    word-break: break-word;
    hyphens: auto;
  }

  &__overlay-link {
    grid-area: link;
  }

  &__overlay-link-tag {
    @include button.buttonWhite();
    @include button.buttonMedium();
  }


  /* MOBILE */
  @include mq.mobileTablet() {
    &__overlay {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;
      aspect-ratio: 1 / 1;
      background-color: transparent;
      border-radius: 4px 4px;
      color: #FFF;
      grid-area: cover;
      grid-template: "play" 1fr "separator" auto "meta" auto "link" 1fr / 100%;
      height: 100%;
      width: 100%;
      gap: .4rem;
    }

    &__overlay * {
      display: flex;
    }

    &__overlay-separator,
    &__overlay-meta {
      display: none;
    }

    &__overlay-play {
      margin-left: 0.8rem;
      margin-bottom: 0.8rem;
      border: 1px solid #000;
      border-radius: 3px;
      background-color: #ffffff99;
      backdrop-filter: blur(2px);
      order: 1;

      & button {
        transform: scale(0.8);
        font-size: 2.5rem;
        filter: invert(1);
      }
    }

    &__overlay-link { 
      margin-bottom: 0.8rem;
      border: 1px solid #000;
      border-radius: 3px;
      background-color: #ffffff99;
      backdrop-filter: blur(2px);
      order: 2;
      flex-grow: 1;

      & a {
        background-color: transparent;
        color: #000;
        border: 0;
        padding: 0;
        font-size: 12px;
        width: 100%;
        text-align: center;
        line-height: 1;
      }
    }

    &__overlay-like {
      margin-right: 0.8rem;
      margin-bottom: 0.8rem;
      border: 1px solid #000;
      border-radius: 3px;
      background-color: #ffffff99;
      backdrop-filter: blur(2px);
      order: 3;

      & button {
        transform: scale(0.8);
        font-size: 2.0rem;
        filter: invert(1);
      }

      &.product-like--selected button {
        filter: none;
      }
    }
  }

}

</style>
